/* ==============================================
   <Seo /> — the per-route <head>.

   Every page component renders one of these. It
   writes the title, description, canonical, Open
   Graph and Twitter tags for that route, replacing
   whatever public/index.html shipped as defaults.

   Two ways to use it:

     <Seo path="/pricing" />          // copy from routes.json
     <Seo title="…" description="…"   // copy passed in, for
          path={`/insights/${slug}`}  // pages that don’t have a
          image={cover} />            // fixed entry (articles)

   Anything not in routes.json, or explicitly marked
   "index": false there, gets a robots noindex so a
   thin utility page can't outrank a real one.
   ============================================== */

import { Helmet } from "react-helmet-async";

import { SITE, absoluteUrl, routeFor } from "./site";

const Seo = ({
  path,
  title,
  /* What a shared link calls this page, when that should not be what Google
     calls it. The <title> is written for search and carries the keywords; a
     card in Slack or a text message is read by a person, and wants the
     product's own words. Set per route as "ogTitle" in routes.json. */
  ogTitle,
  description,
  image,
  /* "website" for marketing pages, "article" for a piece in /insights. */
  type = "website",
  /* Force noindex regardless of the manifest — the 404 page uses this. */
  noindex = false,
  /* The page's Largest Contentful Paint image, if it has one. Preloading it
     starts the download with the stylesheet rather than after the browser
     has found the <img decoding="async"> tag, which is most of the gap between a hero that
     is there and one that arrives. Never pass a below-the-fold image —
     preloading one competes with the hero for the same connection. */
  preloadImage,
  children,
}) => {
  const entry = path ? routeFor(path) : undefined;

  const pageTitle = title || entry?.title || SITE.defaultTitle;
  const socialTitle = ogTitle || entry?.ogTitle || pageTitle;
  const pageDescription =
    description || entry?.description || SITE.defaultDescription;
  const canonical = absoluteUrl(path || "/");

  /* A relative image (an imported asset, say) is resolved against the site
     origin — Open Graph requires an absolute URL or the card renders blank. */
  const ogImage = absoluteUrl(image || SITE.ogImage);

  const blocked = noindex || (entry ? !entry.index : true);

  return (
    <Helmet prioritizeSeoTags>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <link rel="canonical" href={canonical} />

      <meta
        name="robots"
        content={blocked ? "noindex, follow" : "index, follow, max-image-preview:large"}
      />

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:title" content={socialTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content={String(SITE.ogImageWidth)} />
      <meta property="og:image:height" content={String(SITE.ogImageHeight)} />
      <meta property="og:image:alt" content={socialTitle} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={socialTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={ogImage} />

      {preloadImage && (
        <link rel="preload" as="image" href={preloadImage} fetchpriority="high" />
      )}

      {children}
    </Helmet>
  );
};

export default Seo;
